import React, { useState, useEffect, useMemo } from 'react';

import InputLabel, { HtmlTooltip } from '../../../inputfields/InputLabel';
import ThreeWaySwitch from '../../../inputfields/ThreeWaySwitch';
import IProduct, { IFoodlaCategory, ILabel } from '../../../../models/product';
import MultipleGroupedSelect from 'components/inputfields/MultipleGroupedSelect';
import {
  URSPRUNG_LABELS,
  EKOLOGISKT_LABELS,
  HALLBARHET_LABELS,
  HALSA_KOST_LABELS,
  NARINGSPASTAENDE_LABELS,
  FRI_FRAN_ALLERGI_LABELS,
  DIET_VEG_LABELS,
  DJURVANLIGT_LABELS,
  FISK_SKALDJUR_LABELS,
  VARNING_LABELS,
  MILJO_KEM_LABELS,
  MATERIAL_TRA_LABELS,
  TEXTIL_LABELS,
  HYGIEN_KOSMETIK_LABELS,
  mapLabelsByCategory,
} from 'components/constants-ts';
import { Box } from '@material-ui/core';
import { isBlank } from 'utils/helpers-ts';

interface ILabelsProps {
  readOnly?: boolean;
  state: IProduct;
  setState: (state: IProduct) => void;
  approve?: boolean;
  rootCategory?: IFoodlaCategory;
}

interface ILabelField {
  title: string;
  field: string;
  isFood: boolean;
  isNonFood: boolean;
}

const getListData = (labels: ILabelField[], foodlaCategory?: IFoodlaCategory, rootCategory?: IFoodlaCategory) => {
  const filteredLabels = mapLabelsByCategory({ labels, foodlaCategory, rootCategory });
  return filteredLabels.map((label: ILabelField) => {
    return { value: label.field, displayName: label.title };
  });
};

const getProductMarkingsValue = (value: ILabel[] | undefined) => {
  return value?.reduce((result: string[], item) => {
    if (item.state) {
      result.push(item.field);
    }
    return result;
  }, []);
};

export const getProductMarkings = (foodlaCategory?: IFoodlaCategory, rootCategory?: IFoodlaCategory) => {
  return [
    {
      label: 'Ursprung',
      listData: getListData(URSPRUNG_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Ekologiskt',
      listData: getListData(EKOLOGISKT_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Hållbarhet',
      listData: getListData(HALLBARHET_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Hälsa/Kost',
      listData: getListData(HALSA_KOST_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Näringspåstående',
      listData: getListData(NARINGSPASTAENDE_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Fri från (Allergi)',
      listData: getListData(FRI_FRAN_ALLERGI_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Diet/Veg',
      listData: getListData(DIET_VEG_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Djurvänligt',
      listData: getListData(DJURVANLIGT_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Fisk/Skaldjur',
      listData: getListData(FISK_SKALDJUR_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Varning',
      listData: getListData(VARNING_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Miljö/Kem',
      listData: getListData(MILJO_KEM_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Material/Trä',
      listData: getListData(MATERIAL_TRA_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Textil',
      listData: getListData(TEXTIL_LABELS, foodlaCategory, rootCategory),
    },
    {
      label: 'Hygien/Kosmetik',
      listData: getListData(HYGIEN_KOSMETIK_LABELS, foodlaCategory, rootCategory),
    },
  ];
};

export const Labels = ({ readOnly, state, setState, approve, rootCategory }: ILabelsProps) => {
  const [labels, setLabels] = useState<string[] | undefined>([]);

  const hasApprove = (field: string) => {
    if (!approve) return false;
    if (!state?.approve) return false;
    if (state.approve.length === 0) return false;

    const approveItem = state.approve.find(x => x.field === field);

    if (!approveItem) return false;

    return true;
  };

  const handleDropdownChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    const stateName = event.target.name;
    const stateValue = event.target.value;
    const convertedValue = (stateValue as string[]).map(field => ({ field: field, state: true }));
    const value = !stateValue || stateValue === '' ? null : convertedValue;

    stateName && setState({ ...state, [stateName]: value });
  };

  useEffect(() => {
    const productMarkingsValue = getProductMarkingsValue(state.labels);

    setLabels(productMarkingsValue);
  }, [state.labels]);

  const categoryData = useMemo(() => getProductMarkings(state.foodlaCategory, rootCategory), [
    state.foodlaCategory,
    rootCategory,
  ]);

  const displayedLabels = useMemo(() => {
    if (!labels) return [];
    return labels
      .map(label => {
        let item: { value: string; displayName: string } | undefined;
        categoryData.forEach(({ listData }) => {
          listData.forEach(data => {
            if (data.value === label) item = data;
          });
        });
        return item ? { id: label, name: item.displayName } : null;
      })
      .filter((item): item is { id: string; name: string } => item !== null && !isBlank(item.name));
  }, [labels, categoryData]);

  return (
    <>
      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel
          heading="Märkningar"
          text="Välj relevanta märkningar för din produkt. Observera att vissa kräver certifiering."
        />
        <HtmlTooltip
          interactive
          arrow
          placement="top"
          PopperProps={{ style: { zIndex: 1200, padding: 0 } }}
          style={{ padding: 0 }}
          title={
            displayedLabels.length > 0 ? (
              <Box pt={2} m={-1} mb={0}>
                <Box px={2}>Valda märkningar:</Box>
                <Box
                  px={3}
                  p={1}
                  display="flex"
                  flexDirection="column"
                  gridGap={4}
                  fontWeight={400}
                  mt={1}
                  maxHeight={400}
                  overflow="auto"
                >
                  {displayedLabels.map(item => (
                    <Box key={item.id}>- {item.name}</Box>
                  ))}
                </Box>
              </Box>
            ) : (
              ''
            )
          }
        >
          <Box>
            <MultipleGroupedSelect
              readOnly={readOnly}
              disableLabel={true}
              categoryData={categoryData}
              value={labels}
              targetName="labels"
              handleChange={handleDropdownChange}
              showSearch
            />
          </Box>
        </HtmlTooltip>
      </div>
      {hasApprove('labels') && (
        <ThreeWaySwitch readOnly={readOnly} state={state} style={{ marginLeft: 0 }} setState={setState} name="labels" />
      )}
    </>
  );
};

export default Labels;
